import { ToggleGroup, ToggleGroupItem } from '@awpmarine/ui/components/ui/toggle-group';
import * as React from 'react';

import { type NewsItem, NewsRow } from '@/components/news';
import { m } from '@/paraglide/messages';

const pageSize = 12;

export interface NewsCategory {
  name: string;
  count: number;
}

/** The filterable list under the lead story: tag filters, rows, and a button to show more. */
export function NewsList({ items, categories }: { items: NewsItem[]; categories: NewsCategory[] }) {
  const [active, setActive] = React.useState<string | null>(null);
  const [shown, setShown] = React.useState(pageSize);
  const visible = active ? items.filter((item) => item.tags.includes(active)) : items;

  const filters = [
    { name: m.news_filter_all(), count: items.length, value: null },
    ...categories.map((c) => ({ ...c, value: c.name })),
  ];

  return (
    <>
      <ToggleGroup
        value={[active ?? 'all']}
        // Pressing the selected filter again empties the value; that shows everything, as All does.
        onValueChange={(value) => {
          const next = value[0];
          setActive(!next || next === 'all' ? null : next);
          setShown(pageSize);
        }}
        className='-mr-5 mb-6 w-auto overflow-x-auto pr-5 pb-1.5 sm:mr-0 sm:flex-wrap sm:overflow-visible sm:pr-0'
      >
        {filters.map((filter) => (
          <ToggleGroupItem
            key={filter.name}
            value={filter.value ?? 'all'}
            className='h-auto gap-2 rounded-full border border-[#cfd7e2] bg-white px-4 py-2 text-base font-semibold transition-colors hover:border-brand hover:bg-white aria-pressed:border-transparent aria-pressed:bg-brand aria-pressed:text-white aria-pressed:hover:bg-brand'
          >
            {filter.name}
            <span className='font-medium opacity-70'>{filter.count}</span>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className='border-line border-b'>
        {/* Rows past the fold stay in the page, hidden, so the prerender's crawl still finds every article. */}
        {visible.map((item, index) => (
          <NewsRow key={item.slug} item={item} className={index >= shown ? 'hidden' : undefined} />
        ))}
      </div>

      {visible.length > shown && (
        <div className='mt-10 flex justify-center'>
          <button
            type='button'
            onClick={() => setShown(shown + pageSize)}
            className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border border-[#cfd7e2] bg-white px-7 font-semibold transition-colors hover:border-brand hover:text-brand'
          >
            {m.news_show_more()}
          </button>
        </div>
      )}
    </>
  );
}
